import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * UserInterviewPage Component
 * 
 * Displays user interview tools content like the legacy HTML site
 */
const UserInterviewPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // User Interview intro content from the legacy HTML
  const userInterviewIntroHtml = `
    <p>
    User interviews are one of the most effective ways of gathering qualitative insights from users. Understanding not just what they do but why they do things can give invaluable insights into designing a solution.
    </p>

    <p>
    With user interviews, we can understand the problem from the user's perspective and validate if the problem we've framed is indeed a problem for them. We can also gain deeper insights into their needs, behaviors, and motivations.
    </p>

    <p>
    The following tools can help structure and conduct effective user interviews to maximize the insights gained.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const recruitingUsersTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-interview/recruiting-users.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kovZjAA=/" className="link-table">Miro</a>
    </>
  );

  const screenerSurveyTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-interview/screener-survey.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koq5zRg=/" className="link-table">Miro</a>
    </>
  );

  const interviewStructureTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-interview/tab-interview-structure-template.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kouSxJY=/" className="link-table">Miro</a>
    </>
  );

  const questionStructureTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-interview/question-structure.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kotLxwU=/" className="link-table">Miro</a>
    </>
  );

  const theInterviewTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/user-interview/the-interview.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koyDEPs=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const naCreator = <a href="">N/A</a>;
  const stevePortigalCreator = <a href="https://portigal.com/">Steve Portigal</a>;
  const erinDelpCreator = <a href="https://medium.com/@erindelp">Erin Delp</a>;

  return (
    <>
      {/* User Interview Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research#empathise">Empathise</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/empathise/user-interview">User Interview</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={userInterviewIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#recruiting">Recruiting Users</Link></div>
            <div><Link className="tool-section-cta" to="#screener-survey">Screener Survey</Link></div>
            <div><Link className="tool-section-cta" to="#interview-structure">Interview Structure</Link></div>
            <div><Link className="tool-section-cta" to="#question-structure">Question Structure</Link></div>
            <div><Link className="tool-section-cta" to="#the-interview">The Interview</Link></div>
          </div>
        </div>
      </div>

      {/* Recruiting Users Section */}
      <main className="case-study">
        <span className="case-tool-title" id="recruiting">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="recruiting">
            <span className="tool-title-number">
              <Link to="/empathise/user-interview#recruiting">Recruiting Users</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-interview">User Interview</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-interview#recruiting"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-recruiting" 
            id="tab-recruiting-overview" 
            defaultChecked
          />
          <label htmlFor="tab-recruiting-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Interview", "15/08/20", recruitingUsersTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                Finding the right users to interview is crucial for gathering meaningful insights. Recruiting users involves identifying your target audience and creating strategies to reach and engage them.
                </p>
                
                <p>
                A good recruitment strategy ensures that you're talking to users who represent your actual or potential customer base, providing relevant insights for your problem space.
                </p>
                
                <p>
                The recruitment process typically involves defining user criteria, creating recruitment channels, and screening potential participants to ensure they match your target profile.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/recruiting-test-participants/" className="link-table">How to Recruit Participants for Usability Studies</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.userinterviews.com/blog/the-ultimate-guide-to-user-recruitment" className="link-table">The Ultimate Guide to User Recruitment</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-recruiting" 
            id="tab-recruiting-template"
          />
          <label htmlFor="tab-recruiting-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kovZjAA=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Recruiting Users Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Screener Survey Section */}
      <main className="case-study">
        <span className="case-tool-title" id="screener-survey">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="screener-survey">
            <span className="tool-title-number">
              <Link to="/empathise/user-interview#screener-survey">Screener Survey</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-interview">User Interview</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-interview#screener-survey"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-screener-survey" 
            id="tab-screener-survey-overview" 
            defaultChecked
          />
          <label htmlFor="tab-screener-survey-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Interview", "15/08/20", screenerSurveyTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                A screener survey is a short questionnaire designed to identify individuals who match your target user criteria. It helps filter potential participants before investing time in full interviews.
                </p>
                
                <p>
                Effective screener surveys include questions that help identify users with the right experience, behaviors, and challenges related to your problem space. They should be concise yet thorough enough to identify suitable candidates.
                </p>
                
                <p>
                By using screener surveys, you can ensure that your research participants can provide relevant insights while also gathering preliminary data that can inform your interview approach.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/screening-questions/" className="link-table">Writing Effective Screening Questions</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.userinterviews.com/blog/recruiter-series-writing-effective-screener-surveys" className="link-table">Writing Effective Screener Surveys</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-screener-survey" 
            id="tab-screener-survey-template"
          />
          <label htmlFor="tab-screener-survey-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koq5zRg=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Screener Survey Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Interview Structure Section */}
      <main className="case-study">
        <span className="case-tool-title" id="interview-structure">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="interview-structure">
            <span className="tool-title-number">
              <Link to="/empathise/user-interview#interview-structure">Interview Structure</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-interview">User Interview</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-interview#interview-structure"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-interview-structure" 
            id="tab-interview-structure-overview" 
            defaultChecked
          />
          <label htmlFor="tab-interview-structure-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Interview", "15/08/20", interviewStructureTemplates, stevePortigalCreator)}
              <div className="tool-opening">
                <p>
                A well-structured interview helps ensure you cover all necessary topics while maintaining a natural flow of conversation. The structure typically includes an introduction, warm-up questions, core questions, and a conclusion.
                </p>
                
                <p>
                Having a clear structure allows you to progressively build rapport with participants while systematically exploring topics from general to specific. This approach helps participants feel comfortable sharing their experiences.
                </p>
                
                <p>
                An effective interview structure balances between having a plan and allowing flexibility to explore unexpected but valuable insights that emerge during the conversation.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.portigal.com/books/interviewing-users/" className="link-table">Interviewing Users: How to Uncover Compelling Insights</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/user-interviews/" className="link-table">User Interviews: How, When, and Why to Conduct Them</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-interview-structure" 
            id="tab-interview-structure-template"
          />
          <label htmlFor="tab-interview-structure-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kouSxJY=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Interview Structure Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Question Structure Section */}
      <main className="case-study">
        <span className="case-tool-title" id="question-structure">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="question-structure">
            <span className="tool-title-number">
              <Link to="/empathise/user-interview#question-structure">Question Structure</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-interview">User Interview</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-interview#question-structure"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-question-structure" 
            id="tab-question-structure-overview" 
            defaultChecked
          />
          <label htmlFor="tab-question-structure-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Interview", "15/08/20", questionStructureTemplates, erinDelpCreator)}
              <div className="tool-opening">
                <p>
                How questions are phrased significantly impacts the quality of responses you receive. Effective questions are open-ended, neutral, and encourage detailed responses rather than simple yes/no answers.
                </p>
                
                <p>
                The question structure tool provides frameworks for crafting questions that elicit rich, meaningful responses. It helps you avoid leading questions, loaded questions, or questions that might influence participants' answers.
                </p>
                
                <p>
                By structuring questions appropriately, you can encourage participants to share their authentic experiences and perspectives, providing deeper insights into their needs, behaviors, and pain points.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/how-to-conduct-user-interviews" className="link-table">How to Conduct User Interviews</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://medium.com/design-research-methods/asking-the-right-questions-in-user-research-interviews-d7a2c377d4ae" className="link-table">Asking the Right Questions in User Research Interviews</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-question-structure" 
            id="tab-question-structure-template"
          />
          <label htmlFor="tab-question-structure-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kotLxwU=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Question Structure Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* The Interview Section */}
      <main className="case-study">
        <span className="case-tool-title" id="the-interview">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="the-interview">
            <span className="tool-title-number">
              <Link to="/empathise/user-interview#the-interview">The Interview</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/user-interview">User Interview</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/user-interview#the-interview"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-the-interview" 
            id="tab-the-interview-overview" 
            defaultChecked
          />
          <label htmlFor="tab-the-interview-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("User Interview", "15/08/20", theInterviewTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                This template guides you through conducting the actual interview, including how to take notes, record insights, and manage the conversation effectively.
                </p>
                
                <p>
                During the interview, it's important to be fully present, actively listen, and pay attention to both what is said and what remains unsaid. This tool helps you capture key observations while maintaining engagement with the participant.
                </p>
                
                <p>
                The interview is where all your preparation comes together. By following best practices for conducting interviews, you can create a comfortable environment where participants feel free to share honest feedback and experiences.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/interviewing-users/" className="link-table">Interviewing Users</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/how-to-conduct-user-interviews" className="link-table">How to Conduct User Interviews</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.smashingmagazine.com/2020/01/user-interview-questions-ux-research/" className="link-table">30 UX Research Interview Questions</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-the-interview" 
            id="tab-the-interview-template"
          />
          <label htmlFor="tab-the-interview-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koyDEPs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="The Interview Template"
            ></iframe>
          </div>
          
          <input 
            type="radio" 
            name="tabs-the-interview" 
            id="tab-the-interview-tips"
          />
          <label htmlFor="tab-the-interview-tips">Tips</label>
          <div className="tab tab-tools">
            <ul className="bullet-tk">
              <li>Start with a warm introduction and explain the purpose of the interview.</li>
              <li>Ask for permission to record the session if needed.</li>
              <li>Begin with easy, non-threatening questions to build rapport.</li>
              <li>Use silence effectively - pauses often encourage participants to elaborate.</li>
              <li>Practice active listening and show genuine interest in what participants say.</li>
              <li>Ask follow-up questions to explore interesting points deeper.</li>
              <li>Pay attention to non-verbal cues and body language.</li>
              <li>Stay neutral - avoid showing agreement or disagreement with responses.</li>
              <li>Take concise notes during or immediately after the interview.</li>
              <li>Thank participants for their time and insights at the end.</li>
            </ul>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserInterviewPage; 